import React, { useEffect, useState } from "react";
import { fetchApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Phone, Plus, X, Check, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  organizationId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PhoneNumbersManagementProps {
  isAdmin: boolean;
}

export const PhoneNumbersManagement: React.FC<PhoneNumbersManagementProps> = ({
  isAdmin,
}) => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState<
    PhoneNumber[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClaimDialogOpen, setIsClaimDialogOpen] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState<string | null>(null);

  const fetchPhoneNumbers = async () => {
    setIsLoading(true);
    try {
      const [orgResponse, availableResponse] = await Promise.all([
        fetchApi("phone-numbers", "GET"),
        fetchApi("phone-numbers/available", "GET"),
      ]);

      if (orgResponse.ok && orgResponse.data) {
        setPhoneNumbers(orgResponse.data as PhoneNumber[]);
      }

      if (availableResponse.ok && availableResponse.data) {
        setAvailablePhoneNumbers(availableResponse.data as PhoneNumber[]);
      }
    } catch {
      toast.error("Failed to fetch phone numbers");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const handleClaimPhoneNumber = async (phoneNumberId: string) => {
    setIsLoadingAction(phoneNumberId);
    try {
      const response = await fetchApi(
        `phone-numbers/${phoneNumberId}/claim`,
        "POST",
      );

      if (response.ok) {
        toast.success("Phone number claimed successfully");
        await fetchPhoneNumbers();
        setIsClaimDialogOpen(false);
      } else {
        toast.error("Failed to claim phone number");
      }
    } catch {
      toast.error("Failed to claim phone number");
    } finally {
      setIsLoadingAction(null);
    }
  };

  const handleReleasePhoneNumber = async (phoneNumberId: string) => {
    setIsLoadingAction(phoneNumberId);
    try {
      const response = await fetchApi(
        `phone-numbers/${phoneNumberId}/release`,
        "POST",
      );

      if (response.ok) {
        toast.success("Phone number released successfully");
        await fetchPhoneNumbers();
      } else {
        toast.error("Failed to release phone number");
      }
    } catch {
      toast.error("Failed to release phone number");
    } finally {
      setIsLoadingAction(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <h3
          id="phone-numbers-list"
          className="text-lg font-medium scroll-mt-24"
        >
          Your Phone Numbers
        </h3>
        {isAdmin && (
          <Button
            onClick={() => setIsClaimDialogOpen(true)}
            disabled={availablePhoneNumbers.length === 0}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Phone Number
          </Button>
        )}
      </div>

      {phoneNumbers.length === 0 ? (
        <div className="text-center py-8 border rounded-lg">
          <Phone className="mx-auto h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No phone numbers added yet.</p>
          {isAdmin && (
            <p className="text-sm text-muted-foreground mt-1">
              Click &quot;Add Phone Number&quot; to claim a number for your
              organization.
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {phoneNumbers.map((phone) => {
            const isLoading = isLoadingAction === phone.id;
            return (
              <div
                key={phone.id}
                className="border rounded-lg p-4 space-y-3 bg-background shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">
                      {phone.phoneNumber}
                    </span>
                  </div>
                  <Badge variant="default">Active</Badge>
                </div>

                <div className="text-xs text-muted-foreground">
                  Added: {formatDate(phone.createdAt)}
                </div>

                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleReleasePhoneNumber(phone.id)}
                    disabled={isLoading}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Release
                    {isLoading ? (
                      <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                    ) : null}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={isClaimDialogOpen} onOpenChange={setIsClaimDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
            <DialogDescription>
              Select an available phone number to add to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {availablePhoneNumbers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No phone numbers available at this time.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Please contact support to request additional phone numbers.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availablePhoneNumbers.map((phone) => (
                  <div
                    key={phone.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{phone.phoneNumber}</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleClaimPhoneNumber(phone.id)}
                      disabled={isLoadingAction === phone.id}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Claim
                      {isLoadingAction === phone.id ? (
                        <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                      ) : null}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
